import { ARC_OPS, type Arc } from './arc';
import type { Bounds } from './bounds';
import { CIRCLE_OPS, type Circle } from './circle';
import { SEGMENT_OPS, type Segment } from './segment';
import type { Carrier, Measurement, ShapeOps } from './shape-ops';
import { dist, type Vec2 } from './vec2';

export type Shape = Segment | Circle | Arc;
export type ShapeType = Shape['type'];

type OpsTable = { readonly [K in ShapeType]: ShapeOps<Extract<Shape, { type: K }>> };

/** A new shape kind is one module plus one entry here; the compiler checks both. */
const SHAPE_OPS: OpsTable = { line: SEGMENT_OPS, circle: CIRCLE_OPS, arc: ARC_OPS };

export function opsFor<S extends Shape>(shape: S): ShapeOps<S> {
  // The table is keyed by the discriminant the shape carries, so the lookup always matches.
  return SHAPE_OPS[shape.type] as unknown as ShapeOps<S>;
}

export const endpoints = (shape: Shape): Vec2[] => opsFor(shape).endpoints(shape);
export const midpoint = (shape: Shape): Vec2 | null => opsFor(shape).midpoint(shape);
export const center = (shape: Shape): Vec2 | null => opsFor(shape).center(shape);
export const quadrants = (shape: Shape): Vec2[] => opsFor(shape).quadrants(shape);
export const bounds = (shape: Shape): Bounds => opsFor(shape).bounds(shape);
export const carrier = (shape: Shape): Carrier => opsFor(shape).carrier(shape);

export const closestPoint = (shape: Shape, p: Vec2): Vec2 => opsFor(shape).closestPoint(shape, p);
export const distanceTo = (shape: Shape, p: Vec2): number => dist(closestPoint(shape, p), p);

export const liesOn = (shape: Shape, p: Vec2, eps = 1e-6): boolean =>
  opsFor(shape).liesOn(shape, p, eps);

export const perpendicularFrom = (shape: Shape, from: Vec2): Vec2[] =>
  opsFor(shape).perpendicularFrom(shape, from);

export const tangentFrom = (shape: Shape, from: Vec2): Vec2[] =>
  opsFor(shape).tangentFrom(shape, from);

export const offset = <S extends Shape>(shape: S, distance: number): S | null =>
  opsFor(shape).offset(shape, distance);

export const offsetDistance = (shape: Shape, p: Vec2): number =>
  opsFor(shape).offsetDistance(shape, p);

export const translate = <S extends Shape>(shape: S, delta: Vec2): S =>
  opsFor(shape).translate(shape, delta);

export const rotateShape = <S extends Shape>(shape: S, center: Vec2, degrees: number): S =>
  opsFor(shape).rotate(shape, center, degrees);

export const mirrorShape = <S extends Shape>(shape: S, a: Vec2, b: Vec2): S =>
  opsFor(shape).mirror(shape, a, b);

export const parameterOf = (shape: Shape, p: Vec2): number => opsFor(shape).parameterOf(shape, p);

export const measurements = (shape: Shape): Measurement[] => opsFor(shape).measurements(shape);
export const period = (shape: Shape): number | null => opsFor(shape).period(shape);
export const piece = (shape: Shape, t0: number, t1: number): Shape =>
  opsFor(shape).piece(shape, t0, t1);
